import React, { useMemo, useState, useRef, useId } from 'react';
import './WheelGame.css';

const DEFAULT_PRIZES = ['Dessert offert', 'Café gratuit', 'Boisson offerte'];
const SLICE_GIFT_IMG = 'https://commercant.heypulse.fr/layout/images/slice.gif';

const WheelGame = ({
  prizes = [],
  logo,
  businessName,
  message,
  background,
  accent,
  centerSrc,
}) => {
  const clipId = useId().replace(/:/g, '');
  const displayPrizes = useMemo(() => {
    const cleaned = (prizes || [])
      .map((p) => (typeof p === 'string' ? p.trim() : ''))
      .filter(Boolean);

    const pool = cleaned.length > 0 ? cleaned : DEFAULT_PRIZES;
    const filled = [];
    for (let i = 0; i < 5; i++) {
      filled.push({ text: pool[i % pool.length], win: true });
      filled.push({ text: 'PERDU', win: false });
    }
    return filled;
  }, [prizes]);

  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const rotationRef = useRef(0);

  const segmentCount = displayPrizes.length;
  const segmentAngle = 360 / segmentCount;
  const giftColor = accent || '#000000';
  const ctaColor = accent || '#000000';

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setWinner(null);

    const winningIndex = displayPrizes.findIndex((p) => p.win);
    const targetIndex = winningIndex >= 0 ? winningIndex : 0;
    const targetAngle = 360 - (targetIndex * segmentAngle + segmentAngle / 2);
    const spins = 5;
    const finalRotation =
      rotationRef.current + spins * 360 + targetAngle - (rotationRef.current % 360);

    rotationRef.current = finalRotation;
    setRotation(finalRotation);

    setTimeout(() => {
      setSpinning(false);
      setWinner(displayPrizes[targetIndex].text);
    }, 4200);
  };

  const headline =
    message || 'PARTICIPEZ À NOTRE JEU ET TENTEZ DE GAGNER UN CADEAU !';

  const rootStyle = background ? { background } : undefined;

  return (
    <div className="wheel-game wheel-game-live" style={rootStyle}>
      {!winner ? (
        <>
          <div className="wheel-game-top">
            <div className="wheel-game-brand">
              {logo ? (
                <img src={logo} alt={businessName || 'Logo'} className="wheel-game-logo" />
              ) : (
                <div className="wheel-game-logo-fallback">
                  {(businessName || 'H').charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="wheel-game-message-wrap">
              <p className="wheel-game-headline">{headline}</p>
            </div>

            <div className="wheel-game-cta-row">
              <button
                type="button"
                className="wheel-game-cta"
                style={{ background: ctaColor }}
                onClick={spin}
                disabled={spinning}
              >
                {spinning ? 'Rotation...' : 'JOUER LA PARTIE'}
              </button>
            </div>
          </div>

          <div className="wheel-game-stage">
            <div className="wheel-pointer" aria-hidden="true" />
            <div className="wheel-orient">
              <div
                className="wheel-svg-wrap"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: spinning
                    ? 'transform 4s cubic-bezier(0.17, 0.67, 0.16, 1)'
                    : 'none',
                }}
              >
                <svg viewBox="0 0 200 200" width="100%" height="100%">
                  <defs>
                    <clipPath id={`wheel-center-${clipId}`}>
                      <circle cx="100" cy="100" r="22" />
                    </clipPath>
                  </defs>
                  {displayPrizes.map((prize, i) => {
                    const startAngle = i * segmentAngle - 90;
                    const endAngle = (i + 1) * segmentAngle - 90;
                    const start = polarToCartesian(100, 100, 98, startAngle);
                    const end = polarToCartesian(100, 100, 98, endAngle);
                    const pathData = [
                      'M 100 100',
                      `L ${start.x} ${start.y}`,
                      `A 98 98 0 0 1 ${end.x} ${end.y}`,
                      'Z',
                    ].join(' ');

                    const midAngle = startAngle + segmentAngle / 2;
                    const iconPos = polarToCartesian(100, 100, 64, midAngle);
                    const bg = prize.win ? giftColor : '#FFFFFF';

                    return (
                      <g key={i}>
                        <path d={pathData} fill={bg} stroke="#111" strokeWidth="1.5" />
                        {prize.win ? (
                          <image
                            href={SLICE_GIFT_IMG}
                            x={iconPos.x - 14}
                            y={iconPos.y - 14}
                            width="28"
                            height="28"
                            transform={`rotate(${midAngle + 90} ${iconPos.x} ${iconPos.y})`}
                          />
                        ) : (
                          <text
                            x={iconPos.x}
                            y={iconPos.y}
                            fill="#000000"
                            fontSize="9"
                            fontWeight="800"
                            fontFamily="Lilita One, Arial, sans-serif"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            transform={`rotate(${midAngle + 90} ${iconPos.x} ${iconPos.y})`}
                          >
                            PERDU
                          </text>
                        )}
                      </g>
                    );
                  })}
                  {centerSrc ? (
                    <image
                      href={centerSrc}
                      x="70"
                      y="70"
                      width="60"
                      height="60"
                      clipPath={`url(#wheel-center-${clipId})`}
                      preserveAspectRatio="xMidYMid slice"
                    />
                  ) : (
                    <>
                      <circle cx="100" cy="100" r="22" fill="#fff" stroke="#111" strokeWidth="2" />
                      <circle cx="100" cy="100" r="12" fill="#646464" />
                    </>
                  )}
                </svg>
              </div>
            </div>
          </div>

          <div className="wheel-game-footer">
            <span>Contact</span>
            <span>Aide</span>
          </div>
        </>
      ) : (
        <div className="wheel-win-screen">
          <div className="wheel-win-emoji">🎉</div>
          <div className="wheel-win-title">
            {/perdu/i.test(winner) ? 'Dommage…' : 'Vous avez gagné !'}
          </div>
          <div className="wheel-win-prize" style={accent ? { background: accent, color: '#fff' } : undefined}>
            {winner}
          </div>
          <button
            type="button"
            className="wheel-game-cta"
            style={{ background: ctaColor }}
            onClick={() => setWinner(null)}
          >
            JOUER LA PARTIE
          </button>
          <div className="wheel-game-footer">
            <span>Contact</span>
            <span>Aide</span>
          </div>
        </div>
      )}
    </div>
  );
};

function polarToCartesian(cx, cy, r, angleInDegrees) {
  const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
  return {
    x: cx + r * Math.cos(angleInRadians),
    y: cy + r * Math.sin(angleInRadians),
  };
}

export default WheelGame;
